import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiSearch, FiUser } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['link'],
    ['clean']
  ]
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent', 'link'
];

const TaskModal = ({ task, onClose, onSave, users, clients, projects, selectedProject, projectTeam, lockAssignment }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    due_date: '',
    client_id: '',
    project_id: '',
    assigned_users: []
  });
  
  const [userSearch, setUserSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [projectSearch, setProjectSearch] = useState('');
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const projectDropdownRef = useRef(null);
  
  // Debug current state
  console.log('Current form data:', formData);

  useEffect(() => {
    if (task) {
      console.log('Task for editing/prefilling:', task);
      
      // Handle both editing existing task and prefilled new task
      const assignedUserIds = task.Users ? task.Users.map(user => String(user.id)) : [];
      
      setFormData({
        title: task.title || '',
        description: task.description || '',
        status: task.status || 'todo',
        priority: task.priority || 'medium',
        due_date: task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : '',
        client_id: task.client_id ? String(task.client_id) : '',
        project_id: task.project_id ? String(task.project_id) : '',
        assigned_users: assignedUserIds
      });
      
      // Set selected users for display
      const selectedUserObjects = task.Users || [];
      setSelectedUsers(selectedUserObjects);
    } else if (selectedProject) {
      // New task within a specific project context
      setFormData({
        title: '',
        description: '',
        status: 'todo',
        priority: 'medium',
        due_date: '',
        client_id: selectedProject.client_id ? String(selectedProject.client_id) : (selectedProject.Client ? String(selectedProject.Client.id) : ''),
        project_id: String(selectedProject.id),
        assigned_users: []
      });
      setSelectedUsers([]);
      setUserSearch('');
    } else {
      // Reset form for new task without project context
      setFormData({
        title: '',
        description: '',
        status: 'todo',
        priority: 'medium',
        due_date: '',
        client_id: '',
        project_id: '',
        assigned_users: []
      });
      setSelectedUsers([]);
      setUserSearch('');
    }
  }, [task, selectedProject]);

  // Sync project search label when formData.project_id is set
  useEffect(() => {
    if (formData.project_id && projects) {
      const p = projects.find(p => String(p.id) === String(formData.project_id));
      if (p) setProjectSearch(p.name);
    } else {
      setProjectSearch('');
    }
  }, [formData.project_id, projects]);

  // Close project dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(e.target))
        setProjectDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle due_date specially to avoid invalid dates
    if (name === 'due_date') {
      // Only set the date if it's a valid date string or empty
      setFormData(prev => ({ ...prev, [name]: value }));
    } else if ((name === 'client_id' || name === 'project_id') && value === '') {
      setFormData(prev => ({ ...prev, [name]: null }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleUserAdd = (user) => {
    console.log('Adding user:', user);
    if (!selectedUsers.find(u => u.id === user.id)) {
      const newSelectedUsers = [...selectedUsers, user];
      console.log('New selected users:', newSelectedUsers);
      setSelectedUsers(newSelectedUsers);
      const newAssignedIds = newSelectedUsers.map(u => String(u.id));
      console.log('New assigned IDs:', newAssignedIds);
      setFormData(prev => ({
        ...prev,
        assigned_users: newAssignedIds
      }));
    }
    setUserSearch('');
  };
  
  const handleUserRemove = (userId) => {
    console.log('Removing user:', userId);
    const newSelectedUsers = selectedUsers.filter(u => u.id !== userId);
    console.log('Remaining selected users:', newSelectedUsers);
    setSelectedUsers(newSelectedUsers);
    const newAssignedIds = newSelectedUsers.map(u => String(u.id));
    console.log('Remaining assigned IDs:', newAssignedIds);
    setFormData(prev => ({
      ...prev,
      assigned_users: newAssignedIds
    }));
  };
  
  const availableUsers = projectTeam && projectTeam.length > 0 ? projectTeam : (Array.isArray(users) ? users : []);

  const filteredUsers = availableUsers.filter(user => 
    user.name.toLowerCase().includes(userSearch.toLowerCase()) &&
    !selectedUsers.find(u => u.id === user.id)
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('=== TaskModal Submit Debug ===');
    
    // Clean up the form data before submitting
    const cleanedFormData = {
      ...formData,
      // Ensure due_date is either a valid date string or null
      due_date: formData.due_date && formData.due_date.trim() !== '' ? formData.due_date : null,
      // Ensure IDs are properly formatted
      client_id: formData.client_id || null,
      project_id: formData.project_id || null
    };
    
    console.log('Original form data:', formData);
    console.log('Cleaned form data being submitted:', cleanedFormData);
    console.log('Selected users:', selectedUsers);
    console.log('Selected project context:', selectedProject);
    console.log('Current task (edit mode):', task);
    
    // Validate required fields
    if (!cleanedFormData.title || cleanedFormData.title.trim() === '') {
      alert('Task title is required');
      return;
    }
    
    if (!lockAssignment && (!cleanedFormData.assigned_users || cleanedFormData.assigned_users.length === 0)) {
      const confirmCreate = window.confirm('No users are assigned to this task. Do you want to create it anyway?');
      if (!confirmCreate) return;
    }
    
    console.log('Validation passed, calling onSave...');
    onSave(cleanedFormData);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {task ? 'Edit Task' : 'Create Task'}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <FiX size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  Title *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <div className="mt-1">
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                    style={{ height: '180px', marginBottom: '42px' }}
                    placeholder="Enter task description..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="todo">To Do</option>
                    <option value="in_progress">In Progress</option>
                    <option value="testing">Testing</option>
                    <option value="client_review">Client Review</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
                    Priority
                  </label>
                  <select
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="due_date" className="block text-sm font-medium text-gray-700">
                    Due Date
                  </label>
                  <input
                    type="date"
                    id="due_date"
                    name="due_date"
                    value={formData.due_date}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="client_id" className="block text-sm font-medium text-gray-700">
                    Client {selectedProject && <span className="text-xs text-gray-500">(Auto-selected from project)</span>}
                  </label>
                  <select
                    id="client_id"
                    name="client_id"
                    value={formData.client_id}
                    onChange={handleChange}
                    disabled={!!selectedProject}
                    className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${selectedProject ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  >
                    <option value="">No Client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="mb-4">
                <label htmlFor="project_id" className="block text-sm font-medium text-gray-700">
                  Project {selectedProject && <span className="text-xs text-gray-500">(Pre-selected)</span>}
                </label>
                {selectedProject ? (
                  <div className="mt-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm text-gray-600 cursor-not-allowed">
                    {selectedProject.name}
                  </div>
                ) : (
                  <div className="relative mt-1" ref={projectDropdownRef}>
                    <input
                      type="text"
                      value={projectSearch}
                      onChange={e => { setProjectSearch(e.target.value); setProjectDropdownOpen(true); }}
                      onFocus={() => setProjectDropdownOpen(true)}
                      placeholder="Search project..."
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                      autoComplete="off"
                    />
                    {formData.project_id && (
                      <button
                        type="button"
                        onClick={() => { setFormData(prev => ({ ...prev, project_id: '', client_id: '' })); setProjectSearch(''); }}
                        className="absolute right-2 top-2.5 text-gray-400 hover:text-red-500 text-sm"
                      >
                        &times;
                      </button>
                    )}
                    {projectDropdownOpen && (
                      <div className="absolute z-20 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto mt-1">
                        <button
                          type="button"
                          onClick={() => { setFormData(prev => ({ ...prev, project_id: '', client_id: '' })); setProjectSearch(''); setProjectDropdownOpen(false); }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-400 hover:bg-gray-50 border-b"
                        >
                          No Project
                        </button>
                        {(projects || []).filter(p => p.name.toLowerCase().includes(projectSearch.toLowerCase())).map(p => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                project_id: String(p.id),
                                client_id: p.client_id ? String(p.client_id) : (p.Client ? String(p.Client.id) : '')
                              }));
                              setProjectSearch(p.name);
                              setProjectDropdownOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${String(p.id) === String(formData.project_id) ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
                          >
                            {p.name}
                          </button>
                        ))}
                        {(projects || []).filter(p => p.name.toLowerCase().includes(projectSearch.toLowerCase())).length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-400">No projects found</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned Users {projectTeam && projectTeam.length > 0 && <span className="text-xs text-gray-500">(Project Team)</span>}
                </label>

                {lockAssignment ? (
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                    <FiUser size={14} className="text-gray-400" />
                    <span className="text-sm text-gray-600">Assigned to you</span>
                    <span className="ml-auto text-xs text-gray-400">Only you can be assigned</span>
                  </div>
                ) : (
                  <>
                {selectedUsers.length > 0 && (
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-2">
                      {selectedUsers.map(user => (
                        <div key={user.id} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center">
                          <FiUser className="mr-1" size={12} />
                          {user.name}
                          <button
                            type="button"
                            onClick={() => handleUserRemove(user.id)}
                            className="ml-2 text-blue-600 hover:text-blue-800"
                          >
                            <FiX size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* User Search */}
                <div className="relative mb-2">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiSearch className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search users to assign..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                  />
                </div>
                
                {/* User Search Results */}
                {userSearch && (
                  <div className="border border-gray-200 rounded-md max-h-32 overflow-y-auto">
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map(user => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => handleUserAdd(user)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center text-sm border-b border-gray-100 last:border-b-0"
                        >
                          <FiUser className="mr-2" size={14} />
                          <span className="font-medium">{user.name}</span>
                          <span className="ml-2 text-gray-500">({user.email || user.role})</span>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">No users found</div>
                    )}
                  </div>
                )}
                  </>
                )}
              </div>

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="submit"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:col-start-2 sm:text-sm"
                >
                  {task ? 'Update Task' : 'Create Task'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskModal;